import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CircleAlert, Plus, Video } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import {
  allSites,
  connectionInfo,
  devicesKey,
  isRecorder,
  kindLabels,
  listDevices,
  sitesKey,
  statusView,
  type Device,
  type DeviceQuery,
  accountLabel,
  byClient,
} from '../features/inventory/api';
import { DeviceDrawer } from '../features/inventory/DeviceDrawer';
import { SnapshotPreview } from '../features/inventory/SnapshotPreview';
import { listRecordingPolicies, policiesKey } from '../features/recording/api';
import { RecordingCell } from '../features/recording/RecordingCell';
import { readPaging } from '../lib/paging';
import { useTimeFormat } from '../lib/useTimeFormat';
import { useUrlState } from '../lib/useUrlState';

const selectClass =
  'h-8 rounded-md border border-line-strong bg-canvas px-2 text-[13px] text-fg hover:border-fg-subtle/60 focus:border-accent';

type DrawerState = { device?: Device } | null;

export function DevicesPage() {
  const { request, can } = useAuth();
  const [params, update] = useUrlState();
  const query: DeviceQuery = {
    q: params.get('q') ?? '',
    site: params.get('site') ?? '',
    kind: params.get('kind') ?? '',
    status: params.get('status') ?? '',
    sort: 'name',
    ...readPaging(params),
    // Channels are listed inside their recorder, but a search finds them too.
    topLevel: !params.get('q'),
  };
  const canManage = can('devices.manage');
  const fmt = useTimeFormat();
  const [drawer, setDrawer] = useState<DrawerState>(null);

  const devices = useQuery({
    queryKey: [...devicesKey, query],
    queryFn: () => listDevices(request, query),
    placeholderData: keepPreviousData,
    // Status comes from the health monitor; refresh while the tab is visible.
    refetchInterval: 15_000,
  });
  const sites = useQuery({ queryKey: [...sitesKey, 'all'], queryFn: () => allSites(request) });
  const showRecording = can('recordings.view');
  const canPreview = can('live.view');
  const pageIds = devices.data?.items.map((d) => d.id) ?? [];
  const policies = useQuery({
    queryKey: policiesKey(pageIds),
    queryFn: () => listRecordingPolicies(request, pageIds),
    enabled: showRecording && pageIds.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
  const policyOf = new Map((policies.data ?? []).map((p) => [p.device_id, p]));

  const filtered = Boolean(query.q || query.site || query.kind || query.status);
  const noSites = sites.data?.length === 0;

  return (
    <>
      <PageHeader
        title="Dispositivos"
        description="Câmeras, DVRs e NVRs cadastrados. Credenciais ficam criptografadas."
        actions={
          canManage && (
            <Button
              variant="primary"
              disabled={!sites.data || noSites}
              onClick={() => {
                setDrawer({});
              }}
            >
              <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
              Novo dispositivo
            </Button>
          )
        }
      />
      <div className="space-y-3 p-4 sm:p-6">
        <div role="search" className="flex flex-wrap items-center gap-2">
          <SearchInput
            label="Buscar por nome, endereço, fabricante ou modelo"
            placeholder="Buscar dispositivo"
            value={query.q}
            onChange={(v) => {
              update({ q: v });
            }}
          />
          <select
            aria-label="Filtrar por conta"
            value={query.site}
            onChange={(e) => {
              update({ site: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todas as contas</option>
            {byClient(sites.data ?? []).map((g) => (
              <optgroup key={g.client} label={g.client}>
                {g.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <select
            aria-label="Filtrar por tipo"
            value={query.kind}
            onChange={(e) => {
              update({ kind: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todos os tipos</option>
            {Object.entries(kindLabels).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por status"
            value={query.status}
            onChange={(e) => {
              update({ status: e.target.value });
            }}
            className={selectClass}
          >
            <option value="">Todos os status</option>
            {Object.entries(statusView).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          {filtered && (
            <Button
              variant="ghost"
              onClick={() => {
                update({ q: '', site: '', kind: '', status: '' });
              }}
            >
              Limpar filtros
            </Button>
          )}
        </div>

        {/* Fills the screen height, so the list uses the available space and the pagination sits at the bottom. */}
        <div className="flex min-h-[calc(100dvh-15rem)] flex-col overflow-hidden rounded-lg border border-line bg-surface">
          {devices.isError && !devices.data ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar os dispositivos"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void devices.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : devices.data?.items.length === 0 ? (
            noSites && !filtered ? (
              <EmptyState
                icon={Video}
                title="Cadastre um cliente e uma conta primeiro"
                description="Todo dispositivo pertence a uma conta (local monitorado) de um cliente. Crie o cliente e a conta para cadastrar câmeras e gravadores."
                action={
                  <Link
                    to="/clients"
                    className="inline-flex h-8 items-center rounded-md bg-accent-strong px-3 text-[13px] font-medium text-white hover:bg-accent"
                  >
                    Ir para Clientes
                  </Link>
                }
              />
            ) : (
              <EmptyState
                icon={Video}
                title={filtered ? 'Nenhum dispositivo encontrado' : 'Nenhum dispositivo cadastrado'}
                description={
                  filtered
                    ? 'Nenhum dispositivo corresponde aos filtros aplicados.'
                    : 'Cadastre câmeras, DVRs e NVRs para monitorá-los.'
                }
              />
            )
          ) : (
            <div className="flex-1 overflow-x-auto">
              <table
                className="w-full min-w-[820px] text-left text-[13px]"
                aria-busy={devices.isFetching}
              >
                <caption className="sr-only">Dispositivos da organização</caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    {[
                      'Dispositivo',
                      'Local',
                      'Tipo',
                      'Conexão',
                      'Endereço',
                      'Status',
                      ...(showRecording ? ['Gravação'] : []),
                    ].map((h) => (
                      <th key={h} scope="col" className="px-4 py-2 font-medium">
                        {h}
                      </th>
                    ))}
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!devices.data
                    ? Array.from({ length: 6 }, (_, i) => (
                        <tr
                          key={i}
                          aria-hidden="true"
                          className="border-b border-line last:border-0"
                        >
                          {[
                            'w-40',
                            'w-24',
                            'w-14',
                            'w-32',
                            'w-20',
                            ...(showRecording ? ['w-16'] : []),
                            'w-10',
                          ].map((w) => (
                            <td key={w} className="px-4 py-3.5">
                              <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : devices.data.items.map((d) => {
                        const st = statusView[d.status];
                        return (
                          <tr
                            key={d.id}
                            className={`border-b border-line last:border-0 hover:bg-surface-hover/50 ${d.enabled ? '' : 'opacity-60'}`}
                          >
                            <td className="px-4 py-2.5">
                              {canPreview && d.enabled && !isRecorder(d) ? (
                                <SnapshotPreview deviceId={d.id} name={d.name}>
                                  <button
                                    type="button"
                                    disabled={!sites.data}
                                    onClick={() => {
                                      setDrawer({ device: d });
                                    }}
                                    className="font-medium text-fg decoration-fg-subtle underline-offset-2 hover:underline"
                                  >
                                    {d.name}
                                  </button>
                                </SnapshotPreview>
                              ) : (
                                <div className="font-medium text-fg">{d.name}</div>
                              )}
                              <div className="text-xs text-fg-subtle">
                                {[
                                  isRecorder(d)
                                    ? `${String(d.channel_count)} canais`
                                    : d.channel
                                      ? `Canal ${String(d.channel)} do gravador`
                                      : '',
                                  d.vendor,
                                  d.model,
                                ]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-fg-muted">
                              {accountLabel(d.client?.name, d.site.name)}
                            </td>
                            <td className="px-4 py-2.5 text-fg-muted">{kindLabels[d.kind]}</td>
                            <td className="px-4 py-2.5">
                              <span className="inline-flex rounded border border-line-strong px-1.5 py-0.5 text-2xs font-medium text-fg">
                                {connectionInfo(d).label}
                              </span>
                              <div className="mt-0.5 text-2xs text-fg-subtle">
                                {connectionInfo(d).detail}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                              {d.connection === 'rtmp' ? '—' : d.host}
                            </td>
                            <td className="px-4 py-2.5">
                              {d.enabled ? (
                                <div>
                                  <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                                  {d.status_since &&
                                    (d.status === 'offline' || d.status === 'error') && (
                                      <div className="mt-0.5 text-2xs text-fg-subtle">
                                        desde{' '}
                                        <time
                                          dateTime={d.status_since}
                                          title={fmt.absolute(d.status_since)}
                                        >
                                          {fmt.relative(d.status_since)}
                                        </time>
                                      </div>
                                    )}
                                </div>
                              ) : (
                                <StatusBadge tone="neutral">Desabilitado</StatusBadge>
                              )}
                            </td>
                            {showRecording && (
                              <td className="px-4 py-2.5">
                                {isRecorder(d) ? (
                                  <span className="text-xs text-fg-subtle">Por canal</span>
                                ) : (
                                  <RecordingCell policy={policyOf.get(d.id)} />
                                )}
                              </td>
                            )}
                            <td className="px-4 py-2.5 text-right">
                              <Button
                                variant="ghost"
                                className="h-7"
                                disabled={!sites.data}
                                aria-label={`${canManage ? 'Editar' : 'Ver'} ${d.name}`}
                                onClick={() => {
                                  setDrawer({ device: d });
                                }}
                              >
                                {canManage ? 'Editar' : 'Ver'}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          )}
          {devices.data && devices.data.total > 0 && (
            <Pagination
              page={query.page}
              size={query.size}
              total={devices.data.total}
              onPage={(p) => {
                update({ page: String(p) });
              }}
              onSize={(s) => {
                update({ size: String(s) });
              }}
            />
          )}
        </div>
      </div>
      {drawer && sites.data && (
        <DeviceDrawer
          key={drawer.device?.id ?? 'new'}
          {...(drawer.device ? { device: drawer.device } : {})}
          onOpen={(d) => {
            setDrawer({ device: d });
          }}
          sites={sites.data}
          canManage={canManage}
          onClose={() => {
            setDrawer(null);
          }}
        />
      )}
    </>
  );
}
